import {
  AppBar,
  BottomNavigationProps,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Toolbar,
  Typography,
} from "@mui/material";
import { BottomNavigationStylesProvider } from "./styles/BottomNavigationStylesProvider";
import { lighten, Stack } from "@mui/system";
import { useAppSelector } from "../../../../../hooks/store-hooks";
import { useProjetosService } from "../../../../../../use-cases/geo-veles";
import { useEffect, useState } from "react";
import { theme } from "../../../../../styles";
import { Transition } from "../../../../../components";
import { useNavigate } from "react-router-dom";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import isEqual from "lodash/isEqual";

/**
 * Função para normalizar (ordenar) os filtros.
 * Se for array, ordena seus elementos (e os normaliza recursivamente).
 * Se for objeto, retorna um novo objeto com as chaves ordenadas.
 */
const normalizeFilters = (filters: any): any => {
  if (Array.isArray(filters)) {
    // Normaliza cada elemento e ordena o array
    return filters.map(normalizeFilters).sort((a, b) => {
      if (typeof a === "string" && typeof b === "string") {
        return a.localeCompare(b);
      }
      // Se forem números
      if (typeof a === "number" && typeof b === "number") {
        return a - b;
      }
      // Converte para string para comparar objetos simples
      return JSON.stringify(a).localeCompare(JSON.stringify(b));
    });
  } else if (filters && typeof filters === "object") {
    // Para objetos, cria um novo objeto com as chaves ordenadas
    const sortedKeys = Object.keys(filters).sort();
    const normalizedObj: any = {};
    sortedKeys.forEach((key) => {
      normalizedObj[key] = normalizeFilters(filters[key]);
    });
    return normalizedObj;
  }
  // Se for valor primitivo, retorna como está
  return filters;
};

export const BottomNavigation = (props: BottomNavigationProps) => {
  // Redux states
  const {
    setProjetoActiveSection,
    setProjetoAction,
    updateFiltrosProjeto,
    setOpenExportType,
    verificarFiltros,
    resetRelatorioProjeto,
  } = useProjetosService();
  const {
    filtrosSelecionados,
    openExportType,
    projetoAberto,
    relatorioProjeto,
  } = useAppSelector((state) => state.projetos);
  const action = useAppSelector((state) => state.projetos.action);
  const user = useAppSelector((state) => state.user);

  // Local state
  const [activeSection, setActiveSection] = useState<"filtros" | "data">(
    "filtros"
  );
  const [filtroAtual, setFiltroAtual] = useState(filtrosSelecionados); // Armazena o estado atual dos filtros

  // useNavigate
  const navigate = useNavigate();

  // Efeito para comparar filtrosSelecionados com filtroAtual usando normalização e isEqual
  useEffect(() => {
    const normalizedSelecionados = normalizeFilters(filtrosSelecionados);
    const normalizedAtual = normalizeFilters(filtroAtual);

    if (
      !relatorioProjeto ||
      !isEqual(normalizedSelecionados, normalizedAtual) ||
      verificarFiltros() === false
    ) {
      // Se houver diferença, o botão deve ser "Atualizar"

      resetRelatorioProjeto();
      verificarFiltros();
      setProjetoAction("atualizar");
    } else {
      // Se não houver diferença, o botão deve ser "Extrair"

      verificarFiltros();
      setProjetoAction("exportar");
    }
  }, [
    filtrosSelecionados,
    filtroAtual,
    setProjetoActiveSection,
    setProjetoAction,
    verificarFiltros,
  ]);

  useEffect(()=>{
    if(!relatorioProjeto){
      setActiveSection("filtros");
      setProjetoActiveSection({ activeDataSection: "filtros" });
    }
  })
  return (
    <>
      <div>
        <BottomNavigationStylesProvider action={action}>
          <div className="wrapper">
            <div className="navbar">
              {/* Botão de Filtros */}
              <div className={`bottom-navigation-item`}>
                <Button
                  fullWidth
                  sx={{
                    height: "100%",
                    backgroundColor:
                      activeSection === "filtros"
                        ? lighten(theme.palette.primary.main, 0.1)
                        : theme.palette.background.default,
                    color:
                      activeSection === "filtros"
                        ? theme.palette.background.default
                        : theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor:
                        activeSection === "filtros"
                          ? lighten(theme.palette.primary.main, 0.1)
                          : theme.palette.background.default,
                    },
                  }}
                  onClick={() => {
                    setActiveSection("filtros");
                    setProjetoActiveSection({ activeDataSection: "filtros" });
                  }}
                >
                  <Stack alignItems={"center"}>
                    <FilterAltIcon />
                    <Typography>Filtros</Typography>
                  </Stack>
                </Button>
              </div>

              {/* Botão de Detalhes */}
              <div className={`bottom-navigation-item`}>
                <Button                  
                  sx={{
                    height: "100%",
                    backgroundColor:
                      activeSection === "data"
                        ? lighten(theme.palette.primary.main, 0.1)
                        : theme.palette.background.default,
                    color:
                      activeSection === "data"
                        ? theme.palette.background.default
                        : theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor:
                        activeSection === "data"
                          ? lighten(theme.palette.primary.main, 0.1)
                          : theme.palette.background.default,
                    },
                  }}
                  onClick={() => {                   
                    setActiveSection("data");
                    setProjetoActiveSection({ activeDataSection: "dados" });
                  }}
                >
                  <Stack alignItems={"center"}>
                    <TextSnippetIcon />
                    <Typography>Detalhes</Typography>
                  </Stack>
                </Button>
              </div>

              {/* Botão de Atualizar/Extrair */}
              <div
                className="circle"
                //disabled={projetoAberto?.IdUsuario!== user.user?.IdUsuario}
                style={{
                  backgroundColor:
                    action === "atualizar"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                }}
                onClick={() => {
                  const normalizedFiltroAtual = normalizeFilters(filtroAtual);
                  const normalizedSelecionados =
                    normalizeFilters(filtrosSelecionados);

                  if (action === "atualizar") {
                    if (
                      relatorioProjeto === undefined ||
                      !isEqual(normalizedFiltroAtual, normalizedSelecionados)
                    ) {
                      setFiltroAtual(filtrosSelecionados); // Atualiza o filtroAtual
                      updateFiltrosProjeto(); // Atualiza os filtros no Redux
                    }
                  } else {
                    if (relatorioProjeto || action === "exportar") {
                      setOpenExportType(true);
                    }
                  }
                }}
              >
                {action === "atualizar" ? (
                  <>
                    <div className="veles-tooltip">Atualizar</div>
                    <div className="icon-circle">
                      <RefreshIcon style={{ color: "white" }} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="veles-tooltip">Extrair</div>
                    <div className="icon-circle">
                      <DownloadIcon style={{ color: "white" }} />
                    </div>
                  </>
                )}
              </div>
              <div className="circleBackground"></div>
            </div>
          </div>
        </BottomNavigationStylesProvider>
      </div>

      {/* Modal de Exportação */}
      <Grid item xs={12}>
        <Dialog
          open={openExportType}
          onClose={() => setOpenExportType(false)}
          maxWidth={"sm"}
          TransitionComponent={Transition}
          fullWidth
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                textAlign={"center"}
                sx={{ ml: 2, flex: 1 }}
                textTransform="uppercase"
                fontWeight={"bold"}
                variant="body1"
                component="div"
              >
                Escolha como deseja exportar sua lista
              </Typography>
            </Toolbar>
          </AppBar>

          <DialogContent dividers>
            <Grid
              container
              gap={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item xs={8}>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  endIcon={<KeyboardTabIcon />}
                  onClick={() => {
                    navigate(
                      `/geo-veles/projetos/${projetoAberto?.IdVelesGeoProjeto}/extracao`
                    );
                    setOpenExportType(false);
                  }}
                >
                  Extração padrão
                </Button>
              </Grid>
              <Grid item xs={8}>
                <Button
                  fullWidth
                  color="secondary"
                  variant="contained"
                  endIcon={<KeyboardTabIcon />}
                  onClick={() => {
                    navigate(
                      `/geo-veles/projetos/${projetoAberto?.IdVelesGeoProjeto}/recorte`
                    );
                    setOpenExportType(false);
                  }}
                >
                  Criar recortes do projeto
                </Button>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button autoFocus onClick={() => setOpenExportType(false)}>
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
};
